import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircleQuestion, X, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const newSessionId = () => {
  const existing = sessionStorage.getItem("intake_help_sid");
  if (existing) return existing;
  const sid = `intake_help_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  sessionStorage.setItem("intake_help_sid", sid);
  return sid;
};

const WELCOME = {
  role: "assistant",
  content:
    "Hi! I am here to help you understand and complete your assessment. Ask me what any question means and I will break it down with an example. I will guide your thinking, not answer for you.",
};

export const IntakeHelpAssistant = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sid = useRef(newSessionId());
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setBusy(true);
    try {
      const res = await axios.post(`${API}/intake-assistant`, { session_id: sid.current, message: text });
      setMessages((m) => [...m, { role: "assistant", content: res.data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "I could not respond just now. Please try again in a moment." }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid="intake-help-button"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-5 right-5 z-50 inline-flex items-center gap-2 bg-[#041C3E] text-white pl-4 pr-5 py-3.5 shadow-xl hover:bg-[#D94833] transition-colors duration-300 font-semibold text-sm"
      >
        {open ? <X size={18} /> : <MessageCircleQuestion size={18} />}
        {open ? "Close" : "Need help?"}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="intake-help-panel"
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-5 z-50 w-[calc(100vw-2.5rem)] sm:w-96 h-[28rem] bg-white border border-[#E5E7EB] shadow-2xl flex flex-col"
          >
            <div className="bg-[#041C3E] text-white px-4 py-3">
              <p className="font-head font-bold text-sm">Assessment help assistant</p>
              <p className="text-xs text-white/60">The Next Move by Rhellex</p>
            </div>
            <div ref={scrollRef} data-testid="intake-help-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#F9F6F1]">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <p className={`max-w-[85%] px-3 py-2 text-sm leading-relaxed ${
                    m.role === "user" ? "bg-[#041C3E] text-white" : "bg-white text-[#162030] border border-[#E5E7EB]"
                  }`}>
                    {m.content}
                  </p>
                </div>
              ))}
              {busy && (
                <div className="flex justify-start">
                  <p className="bg-white border border-[#E5E7EB] px-3 py-2 text-sm text-[#9CA3AF] flex items-center gap-2">
                    <Loader2 size={14} className="animate-spin" /> Thinking
                  </p>
                </div>
              )}
            </div>
            <form onSubmit={send} className="border-t border-[#E5E7EB] p-3 flex items-center gap-2 bg-white">
              <input
                data-testid="intake-help-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about any question..."
                className="flex-1 bg-[#F9F6F1] border border-[#E5E7EB] focus:border-[#041C3E] outline-none px-3 py-2.5 text-sm"
              />
              <button type="submit" data-testid="intake-help-send" disabled={busy || !input.trim()}
                className="bg-[#D94833] text-white p-2.5 hover:bg-[#C03F2A] transition-colors disabled:opacity-40">
                <Send size={16} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
